import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Plus, 
  Edit, 
  Eye, 
  Barcode,
  ChevronDown,
  X,
  Loader2,
  Trash2,
  Upload,
  Image,
  Printer,
  CheckSquare,
  Square
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getAssets, createAsset, updateAsset, deleteAsset } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import JsBarcode from 'jsbarcode';

const CATEGORIES = ['Plant', 'Tools', 'Access Equipment'];
const STATUSES = ['available', 'on_hire', 'damaged', 'maintenance'];

const StatusBadge = ({ status }) => {
  const styles = {
    available: 'badge-success',
    on_hire: 'badge-info',
    damaged: 'badge-error',
    maintenance: 'badge-warning',
  };
  return (
    <span className={`${styles[status] || 'badge-neutral'} inline-block`}>
      {status.replace('_', ' ')}
    </span>
  );
};

const BarcodeDisplay = ({ assetId, name }) => {
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (barcodeRef.current) {
      JsBarcode(barcodeRef.current, assetId, {
        format: 'CODE128',
        width: 2,
        height: 80,
        displayValue: true,
        fontSize: 14,
        margin: 10,
      });
    }
  }, [assetId]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Barcode - ${assetId}</title>
          <style>
            body { font-family: sans-serif; text-align: center; padding: 20px; }
            h2 { margin-bottom: 10px; }
          </style>
        </head>
        <body>
          <h2>${name}</h2>
          <svg id="barcode"></svg>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"><\/script>
          <script>
            JsBarcode("#barcode", "${assetId}", { format: "CODE128", width: 2, height: 100, displayValue: true });
            setTimeout(() => window.print(), 500);
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="flex flex-col items-center">
      <svg ref={barcodeRef}></svg>
      <Button onClick={handlePrint} variant="outline" size="sm" className="mt-2">
        Print Barcode
      </Button>
    </div>
  );
};

export default function AssetsPage() {
  const { user } = useAuth();
  const canDelete = user?.role === 'admin' || user?.role === 'manager';
  
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [barcodeDialog, setBarcodeDialog] = useState({ open: false, asset: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, asset: null });
  const [editingAsset, setEditingAsset] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Barcode printing state
  const [printDialog, setPrintDialog] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [barcodeQuantities, setBarcodeQuantities] = useState({});
  const [printLayout, setPrintLayout] = useState('3x7'); // 3 columns x 7 rows = 21 per page
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Tools',
    make: '',
    model: '',
    serial_number: '',
    location: '',
    next_test_due: '',
    photo_url: '',
    is_external_hire: false,
    external_supplier: '',
    hire_rate_weekly: '',
  });

  const fetchAssets = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;
      if (statusFilter) params.status = statusFilter;
      
      const response = await getAssets(params);
      setAssets(response.data);
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [search, categoryFilter, statusFilter]);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setFormData({ ...formData, photo_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAsset) {
        await updateAsset(editingAsset.id, formData);
        toast.success('Asset updated successfully');
      } else {
        await createAsset(formData);
        toast.success('Asset created successfully');
      }
      setDialogOpen(false);
      setEditingAsset(null);
      resetForm();
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save asset');
    }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setPhotoPreview(asset.photo_url);
    setFormData({
      name: asset.name,
      description: asset.description,
      category: asset.category,
      make: asset.make,
      model: asset.model,
      serial_number: asset.serial_number,
      location: asset.location,
      next_test_due: asset.next_test_due,
      photo_url: asset.photo_url,
      is_external_hire: asset.is_external_hire || false,
      external_supplier: asset.external_supplier || '',
      hire_rate_weekly: asset.hire_rate_weekly || '',
    });
    setDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.asset) return;
    try {
      await deleteAsset(deleteDialog.asset.id);
      toast.success('Asset deleted successfully');
      setDeleteDialog({ open: false, asset: null });
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete asset');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'Tools',
      make: '',
      model: '',
      serial_number: '',
      location: '',
      next_test_due: '',
      photo_url: '',
      is_external_hire: false,
      external_supplier: '',
      hire_rate_weekly: '',
    });
    setPhotoPreview(null);
  };

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setStatusFilter('');
  };

  const isTestDueSoon = (dateStr) => {
    if (!dateStr) return false;
    const dueDate = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  const isTestOverdue = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  // Barcode printing functions
  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => {
      if (prev.includes(assetId)) {
        return prev.filter(id => id !== assetId);
      } else {
        // Initialize quantity to 1 when selecting
        if (!barcodeQuantities[assetId]) {
          setBarcodeQuantities(q => ({ ...q, [assetId]: 1 }));
        }
        return [...prev, assetId];
      }
    });
  };

  const selectAllAssets = () => {
    if (selectedAssets.length === assets.length) {
      setSelectedAssets([]);
      setBarcodeQuantities({});
    } else {
      const allIds = assets.map(a => a.asset_id);
      setSelectedAssets(allIds);
      const quantities = {};
      allIds.forEach(id => quantities[id] = 1);
      setBarcodeQuantities(quantities);
    }
  };

  const updateQuantity = (assetId, qty) => {
    const newQty = Math.max(1, Math.min(50, parseInt(qty) || 1));
    setBarcodeQuantities(prev => ({ ...prev, [assetId]: newQty }));
  };

  const getTotalBarcodes = () => {
    return selectedAssets.reduce((total, assetId) => {
      return total + (barcodeQuantities[assetId] || 1);
    }, 0);
  };

  const getLayoutConfig = () => {
    const layouts = {
      '2x5': { cols: 2, rows: 5, width: 90, height: 55, fontSize: 12 },
      '3x7': { cols: 3, rows: 7, width: 60, height: 38, fontSize: 10 },
      '4x10': { cols: 4, rows: 10, width: 45, height: 27, fontSize: 8 },
    };
    return layouts[printLayout] || layouts['3x7'];
  };

  const printBarcodes = () => {
    if (selectedAssets.length === 0) {
      toast.error('Please select at least one asset');
      return;
    }

    const layout = getLayoutConfig();
    const barcodesPerPage = layout.cols * layout.rows;
    
    // Build array of barcodes to print
    const barcodesToPrint = [];
    selectedAssets.forEach(assetId => {
      const asset = assets.find(a => a.asset_id === assetId);
      if (asset) {
        const qty = barcodeQuantities[assetId] || 1;
        for (let i = 0; i < qty; i++) {
          barcodesToPrint.push(asset);
        }
      }
    });

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Barcodes - ConstructHire</title>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"><\/script>
          <style>
            @page {
              size: A4;
              margin: 10mm;
            }
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: Arial, sans-serif;
            }
            .page {
              width: 190mm;
              height: 277mm;
              page-break-after: always;
              display: grid;
              grid-template-columns: repeat(${layout.cols}, 1fr);
              grid-template-rows: repeat(${layout.rows}, 1fr);
              gap: 2mm;
            }
            .page:last-child {
              page-break-after: auto;
            }
            .barcode-label {
              border: 1px solid #ccc;
              padding: 2mm;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              overflow: hidden;
            }
            .barcode-label svg {
              max-width: 100%;
              height: auto;
            }
            .asset-name {
              font-size: ${layout.fontSize}px;
              font-weight: bold;
              text-align: center;
              margin-bottom: 1mm;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              max-width: 100%;
            }
            .asset-id {
              font-size: ${layout.fontSize - 2}px;
              color: #666;
              text-align: center;
            }
            @media print {
              .no-print { display: none; }
            }
            .print-header {
              padding: 10px 20px;
              background: #f5f5f5;
              border-bottom: 1px solid #ddd;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .print-btn {
              padding: 10px 20px;
              background: #FF5F00;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-weight: bold;
            }
            .print-btn:hover {
              background: #e55500;
            }
          </style>
        </head>
        <body>
          <div class="print-header no-print">
            <div>
              <strong>ConstructHire Barcode Labels</strong>
              <span style="color: #666; margin-left: 10px;">${barcodesToPrint.length} labels (${Math.ceil(barcodesToPrint.length / barcodesPerPage)} page${Math.ceil(barcodesToPrint.length / barcodesPerPage) > 1 ? 's' : ''})</span>
            </div>
            <button class="print-btn" onclick="window.print()">Print Labels</button>
          </div>
          <div id="pages"></div>
          <script>
            const barcodes = ${JSON.stringify(barcodesToPrint.map(a => ({ id: a.asset_id, name: a.name })))};
            const barcodesPerPage = ${barcodesPerPage};
            const pagesContainer = document.getElementById('pages');
            
            // Create pages
            const numPages = Math.ceil(barcodes.length / barcodesPerPage);
            for (let p = 0; p < numPages; p++) {
              const page = document.createElement('div');
              page.className = 'page';
              
              for (let i = 0; i < barcodesPerPage; i++) {
                const idx = p * barcodesPerPage + i;
                const label = document.createElement('div');
                label.className = 'barcode-label';
                
                if (idx < barcodes.length) {
                  const barcode = barcodes[idx];
                  label.innerHTML = \`
                    <div class="asset-name">\${barcode.name}</div>
                    <svg id="barcode-\${p}-\${i}"></svg>
                  \`;
                  page.appendChild(label);
                  
                  // Generate barcode after adding to DOM
                  setTimeout(() => {
                    try {
                      JsBarcode("#barcode-" + p + "-" + i, barcode.id, {
                        format: "CODE128",
                        width: 1.5,
                        height: ${layout.height},
                        displayValue: true,
                        fontSize: ${layout.fontSize},
                        margin: 2,
                        textMargin: 1
                      });
                    } catch(e) {
                      console.error('Barcode error:', e);
                    }
                  }, 100);
                } else {
                  // Empty cell
                  label.style.border = 'none';
                  page.appendChild(label);
                }
              }
              pagesContainer.appendChild(page);
            }
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
    
    setPrintDialog(false);
    setSelectedAssets([]);
    setBarcodeQuantities({});
    toast.success(`Ready to print ${barcodesToPrint.length} barcode labels`);
  };

  return (
    <div className="space-y-6" data-testid="assets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-3xl text-slate-900 uppercase tracking-tight">
            Asset Register
          </h1>
          <p className="font-body text-slate-500 mt-1">
            {assets.length} assets total
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setPrintDialog(true)}
            className="border-2 border-slate-200 font-heading font-bold uppercase tracking-wide"
            data-testid="print-barcodes-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print Barcodes
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingAsset(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary hover:bg-primary-hover text-white font-heading font-bold uppercase tracking-wide shadow-industrial-sm"
                data-testid="add-asset-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Asset
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg border-2 border-slate-200 shadow-industrial max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold uppercase">
                {editingAsset ? 'Edit Asset' : 'Add New Asset'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Photo Upload */}
              <div>
                <Label className="font-heading font-bold uppercase text-xs">Asset Photo</Label>
                <div className="mt-2">
                  {photoPreview ? (
                    <div className="relative">
                      <img 
                        src={photoPreview} 
                        alt="Asset preview" 
                        className="w-full h-40 object-cover rounded border-2 border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => { setPhotoPreview(null); setFormData({ ...formData, photo_url: '' }); }}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded cursor-pointer hover:border-primary transition-colors">
                      <Image className="w-8 h-8 text-slate-400 mb-2" />
                      <span className="font-body text-sm text-slate-500">Click to upload photo</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="font-heading font-bold uppercase text-xs">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1 border-2 border-slate-200"
                    data-testid="asset-name-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="font-heading font-bold uppercase text-xs">Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 border-2 border-slate-200"
                  />
                </div>
                <div>
                  <Label className="font-heading font-bold uppercase text-xs">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="mt-1 border-2 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-heading font-bold uppercase text-xs">Make</Label>
                  <Input
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    className="mt-1 border-2 border-slate-200"
                  />
                </div>
                <div>
                  <Label className="font-heading font-bold uppercase text-xs">Model</Label>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="mt-1 border-2 border-slate-200"
                  />
                </div>
                <div>
                  <Label className="font-heading font-bold uppercase text-xs">Serial Number</Label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    className="mt-1 border-2 border-slate-200"
                  />
                </div>
                <div>
                  <Label className="font-heading font-bold uppercase text-xs">Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="mt-1 border-2 border-slate-200"
                  />
                </div>
                <div>
                  <Label className="font-heading font-bold uppercase text-xs">Next Test Due</Label>
                  <Input
                    type="date"
                    value={formData.next_test_due}
                    onChange={(e) => setFormData({ ...formData, next_test_due: e.target.value })}
                    className="mt-1 border-2 border-slate-200"
                  />
                </div>
                <div>
                  <Label className="font-heading font-bold uppercase text-xs">Weekly Hire Rate (£)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.hire_rate_weekly}
                    onChange={(e) => setFormData({ ...formData, hire_rate_weekly: parseFloat(e.target.value) || '' })}
                    placeholder="0.00"
                    className="mt-1 border-2 border-slate-200"
                    data-testid="hire-rate-input"
                  />
                </div>
                
                {/* External Hire Section */}
                <div className="col-span-2 border-t-2 border-slate-200 pt-4 mt-2">
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      id="is_external_hire"
                      checked={formData.is_external_hire}
                      onChange={(e) => setFormData({ ...formData, is_external_hire: e.target.checked })}
                      className="w-5 h-5 rounded border-2 border-slate-300 text-primary focus:ring-primary"
                      data-testid="external-hire-checkbox"
                    />
                    <Label htmlFor="is_external_hire" className="font-heading font-bold uppercase text-xs cursor-pointer">
                      External Hire (Hired from external supplier)
                    </Label>
                  </div>
                  
                  {formData.is_external_hire && (
                    <div>
                      <Label className="font-heading font-bold uppercase text-xs">Supplier Name</Label>
                      <Input
                        value={formData.external_supplier}
                        onChange={(e) => setFormData({ ...formData, external_supplier: e.target.value })}
                        placeholder="Enter supplier company name"
                        className="mt-1 border-2 border-slate-200"
                        data-testid="external-supplier-input"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="save-asset-btn">
                  {editingAsset ? 'Update' : 'Create'} Asset
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search & Filters */}
      <Card className="border-2 border-slate-200 shadow-industrial">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by name, ID, or serial number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 border-2 border-slate-200"
                data-testid="search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="h-12 border-2 border-slate-200"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-200">
              <div>
                <Label className="font-heading font-bold uppercase text-xs">Category</Label>
                <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="mt-1 border-2 border-slate-200">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-heading font-bold uppercase text-xs">Status</Label>
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="mt-1 border-2 border-slate-200">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>{status.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="ghost" onClick={clearFilters} className="h-10">
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assets Table */}
      <Card className="border-2 border-slate-200 shadow-industrial overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : assets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Image className="w-12 h-12 mb-2" />
              <p>No assets found</p>
            </div>
          ) : (
            <table className="table-industrial">
              <thead>
                <tr>
                  <th>Asset ID</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Rate/Week</th>
                  <th>Location</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((asset) => (
                  <tr key={asset.id} data-testid={`asset-row-${asset.asset_id}`}>
                    <td className="font-mono text-sm">{asset.asset_id}</td>
                    <td>
                      <div className="flex items-center gap-3">
                        <img
                          src={asset.photo_url || 'https://images.unsplash.com/photo-1632095710940-ad578e8cbe6b?w=50'}
                          alt={asset.name}
                          className="w-10 h-10 rounded object-cover border border-slate-200"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-body font-semibold">{asset.name}</p>
                            {asset.is_external_hire && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-purple-100 text-purple-700 rounded">
                                External
                              </span>
                            )}
                          </div>
                          {asset.make && <p className="text-xs text-slate-500">{asset.make} {asset.model}</p>}
                          {asset.is_external_hire && asset.external_supplier && (
                            <p className="text-xs text-purple-600">Supplier: {asset.external_supplier}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="badge-neutral">{asset.category}</span>
                    </td>
                    <td>
                      <StatusBadge status={asset.status} />
                    </td>
                    <td className="font-mono text-sm">
                      {asset.hire_rate_weekly > 0 ? `£${asset.hire_rate_weekly.toFixed(2)}` : '-'}
                    </td>
                    <td className="text-sm">{asset.location || '-'}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setBarcodeDialog({ open: true, asset })}
                          title="View Barcode"
                        >
                          <Barcode className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(asset)}
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Link to={`/assets/${asset.asset_id}`}>
                          <Button variant="ghost" size="sm" title="View Details">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDialog({ open: true, asset })}
                            title="Delete"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Barcode Dialog */}
      <Dialog open={barcodeDialog.open} onOpenChange={(open) => setBarcodeDialog({ open, asset: null })}>
        <DialogContent className="max-w-md border-2 border-slate-200 shadow-industrial">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold uppercase">
              Asset Barcode
            </DialogTitle>
          </DialogHeader>
          {barcodeDialog.asset && (
            <BarcodeDisplay 
              assetId={barcodeDialog.asset.asset_id} 
              name={barcodeDialog.asset.name} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, asset: null })}>
        <AlertDialogContent className="border-2 border-slate-200 shadow-industrial">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading font-bold uppercase">Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteDialog.asset?.name}</strong> ({deleteDialog.asset?.asset_id})?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Print Barcodes Dialog */}
      <Dialog open={printDialog} onOpenChange={setPrintDialog}>
        <DialogContent className="max-w-2xl border-2 border-slate-200 shadow-industrial max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold uppercase">
              Print Barcode Labels
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Layout Selection */}
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded border">
              <Label className="font-heading font-bold uppercase text-xs whitespace-nowrap">Label Size:</Label>
              <Select value={printLayout} onValueChange={setPrintLayout}>
                <SelectTrigger className="w-48 border-2 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2x5">Large (2×5 = 10 per page)</SelectItem>
                  <SelectItem value="3x7">Medium (3×7 = 21 per page)</SelectItem>
                  <SelectItem value="4x10">Small (4×10 = 40 per page)</SelectItem>
                </SelectContent>
              </Select>
              <div className="ml-auto text-right">
                <p className="font-mono text-sm">
                  <span className="text-slate-500">Selected:</span>{' '}
                  <span className="font-bold text-primary">{getTotalBarcodes()}</span> labels
                </p>
              </div>
            </div>

            {/* Select All */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllAssets}
                className="border-2 border-slate-200"
              >
                {selectedAssets.length === assets.length ? (
                  <>
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Deselect All
                  </>
                ) : (
                  <>
                    <Square className="w-4 h-4 mr-2" />
                    Select All ({assets.length})
                  </>
                )}
              </Button>
            </div>

            {/* Asset Selection List */}
            <div className="border-2 border-slate-200 rounded max-h-64 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left font-heading font-bold uppercase text-xs px-3 py-2 w-10">
                      <input
                        type="checkbox"
                        checked={selectedAssets.length === assets.length && assets.length > 0}
                        onChange={selectAllAssets}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                    </th>
                    <th className="text-left font-heading font-bold uppercase text-xs px-3 py-2">Asset</th>
                    <th className="text-left font-heading font-bold uppercase text-xs px-3 py-2 w-24">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map((asset) => (
                    <tr 
                      key={asset.asset_id} 
                      className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${
                        selectedAssets.includes(asset.asset_id) ? 'bg-primary/5' : ''
                      }`}
                      onClick={() => toggleAssetSelection(asset.asset_id)}
                    >
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedAssets.includes(asset.asset_id)}
                          onChange={() => toggleAssetSelection(asset.asset_id)}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div>
                          <p className="font-body font-semibold text-sm">{asset.name}</p>
                          <p className="font-mono text-xs text-slate-500">{asset.asset_id}</p>
                        </div>
                      </td>
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        {selectedAssets.includes(asset.asset_id) && (
                          <Input
                            type="number"
                            min="1"
                            max="50"
                            value={barcodeQuantities[asset.asset_id] || 1}
                            onChange={(e) => updateQuantity(asset.asset_id, e.target.value)}
                            className="w-16 h-8 text-center border-2 border-slate-200"
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            {selectedAssets.length > 0 && (
              <div className="p-3 bg-green-50 border-2 border-green-200 rounded">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-heading font-bold text-green-800">
                      {selectedAssets.length} asset{selectedAssets.length > 1 ? 's' : ''} selected
                    </p>
                    <p className="font-mono text-xs text-green-600">
                      {getTotalBarcodes()} total labels &bull; {Math.ceil(getTotalBarcodes() / (getLayoutConfig().cols * getLayoutConfig().rows))} page{Math.ceil(getTotalBarcodes() / (getLayoutConfig().cols * getLayoutConfig().rows)) > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => setPrintDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={printBarcodes}
              disabled={selectedAssets.length === 0}
              className="bg-primary hover:bg-primary-hover"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print {getTotalBarcodes()} Labels
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
